// Lets users choose report dates on a calendar.
// The same calendar supports ranges of days, whole weeks, or whole months. Users
// can choose the start and end by clicking calendar periods, or use a date shortcut
// such as Last 7 days instead of finding those dates manually.
import { PointerEvent, useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import { Dialog } from '../../../components/dialog-portal/dialog.tsx';
import { now } from '../../../../shared/utils/dateUtils.ts';
import { PeriodOptionSet } from './createPeriodOptions.ts';
import { PeriodOption, Resolution } from '../report-settings/reportSettings.ts';
import { findPeriodRangeForDates, getQuickRangeDates, QuickRangeKey } from './periodRanges.ts';
import { getPeriods } from './getPeriods.ts';
import {
    DraftRange,
    mapDraftToResolution,
    hasRangeChanged,
    selectDraftPeriod,
} from './draftRange.ts';
import {
    buildCalendar,
    buildPeriodIndex,
    findQuarterIndex,
    getQuarterWindowStart,
    REPORT_TIME_ZONE,
    VISIBLE_QUARTER_COUNT,
} from './calendarModel.ts';
import { formatBoundary, parsePeriodIndex } from './rangePreview.ts';
import { CalendarMonth } from './calendarMonth.tsx';
import { CalendarNavigation } from './calendarNavigation.tsx';
import { RangeToolbar } from './rangeToolbar.tsx';
import { RangeBoundary } from './rangeBoundary.tsx';
import { useRangePreview } from './useRangePreview.ts';

interface ChoicePrompt {
    periodIndex: number;
    x: number;
    y: number;
}

interface DialogProps {
    first: PeriodOption;
    last: PeriodOption;
    periodOptionSet: PeriodOptionSet;
    initialResolution: Resolution;
    hideResolution?: boolean;
    onApply: (resolution: Resolution, first: PeriodOption, last: PeriodOption) => void;
    onClose: () => void;
}

export function DateRangeDialog({
    first,
    last,
    periodOptionSet,
    initialResolution,
    hideResolution = false,
    onApply,
    onClose,
}: DialogProps) {
    // These are the dates and Days/Weeks/Months choice being edited in the dialog.
    // Keep this draft separate from the report settings so Cancel can discard it.
    const [resolution, setResolution] = useState(initialResolution);
    const initialPeriods = getPeriods(periodOptionSet, initialResolution);
    const [draft, setDraft] = useState<DraftRange>({
        firstIndex: initialPeriods.indexOf(first),
        lastIndex: initialPeriods.indexOf(last),
    });
    const [choicePrompt, setChoicePrompt] = useState<ChoicePrompt | null>(null);
    const periods = getPeriods(periodOptionSet, resolution);
    const todayIsoDate = useMemo(
        () => DateTime.fromJSDate(now()).setZone(REPORT_TIME_ZONE).toISODate()!,
        [],
    );
    const calendar = useMemo(() => buildCalendar(periodOptionSet), [periodOptionSet]);
    const initialQuarterIndex = findQuarterIndex(calendar.quarters, last);
    const [visibleQuarterStart, setVisibleQuarterStart] = useState(
        getQuarterWindowStart(initialQuarterIndex, calendar.quarters.length),
    );
    const visibleQuarters = calendar.quarters.slice(
        visibleQuarterStart,
        visibleQuarterStart + VISIBLE_QUARTER_COUNT,
    );
    const periodIndexByStart = useMemo(() => buildPeriodIndex(periods), [periods]);
    const preview = useRangePreview(draft, periods, resolution);

    function changeResolution(newResolution: Resolution) {
        const isSameResolution = newResolution === resolution;
        if (isSameResolution) return;
        const newPeriods = getPeriods(periodOptionSet, newResolution);
        const mappedDraft = mapDraftToResolution(draft, periods, newPeriods);
        preview.clearPreview();
        setChoicePrompt(null);
        setResolution(newResolution);
        setDraft(mappedDraft);
        const targetIndex = mappedDraft.lastIndex ?? mappedDraft.firstIndex;
        showPeriod(newPeriods[targetIndex ?? -1]);
    }

    function applyQuickRange(key: QuickRangeKey) {
        const dates = getQuickRangeDates(key, now());
        const range = findPeriodRangeForDates(periods, dates.start, dates.end);
        if (!range) return;
        setChoicePrompt(null);
        setDraft(range);
        showPeriod(periods[range.lastIndex]);
    }

    function clearRange() {
        setChoicePrompt(null);
        setDraft({ firstIndex: null, lastIndex: null });
    }

    function handleCalendarClick(event: PointerEvent<HTMLDivElement>) {
        const target = (event.target as HTMLElement).closest<HTMLElement>('[data-period-index]');
        const periodIndex = parsePeriodIndex(target);
        if (periodIndex == null) return;

        const selection = selectDraftPeriod(draft, periodIndex);
        if (selection.needsBoundaryChoice) {
            // A click between the selected start and end dates is ambiguous. Ask whether
            // the clicked day/week/month should replace the start or the end date.
            setChoicePrompt({ periodIndex, x: event.clientX, y: event.clientY });
            return;
        }
        setDraft(selection.range);
    }

    function chooseBoundary(boundary: 'start' | 'end') {
        const canChooseBoundary = choicePrompt != null
            && draft.firstIndex != null
            && draft.lastIndex != null;
        if (!canChooseBoundary) return;
        setDraft(boundary === 'start'
            ? { firstIndex: choicePrompt.periodIndex, lastIndex: draft.lastIndex }
            : { firstIndex: draft.firstIndex, lastIndex: choicePrompt.periodIndex });
        setChoicePrompt(null);
    }

    function showPeriod(period: PeriodOption | undefined) {
        const quarterIndex = findQuarterIndex(calendar.quarters, period);
        setCalendarWindow(getQuarterWindowStart(quarterIndex, calendar.quarters.length));
    }

    function moveCalendarWindow(offset: number) {
        setCalendarWindow(visibleQuarterStart + offset);
    }

    function showYear(year: number) {
        const lastQuarterInYear = calendar.quarters.findLastIndex((quarter) => quarter.year === year);
        setCalendarWindow(getQuarterWindowStart(lastQuarterInYear, calendar.quarters.length));
    }

    function setCalendarWindow(start: number) {
        const maximumStart = Math.max(0, calendar.quarters.length - VISIBLE_QUARTER_COUNT);
        const nextStart = Math.max(0, Math.min(start, maximumStart));
        if (nextStart === visibleQuarterStart) return;
        preview.resetPreview();
        setChoicePrompt(null);
        // Browsing to other quarters replaces the visible days/weeks/months. Remove
        // references to the old elements before registering the new calendar cells.
        preview.clearCells();
        setVisibleQuarterStart(nextStart);
    }

    const canApply = draft.firstIndex != null && draft.lastIndex != null;
    const isDirty = hasRangeChanged(draft, periods, resolution, first, last, initialResolution);
    const canMoveBackward = visibleQuarterStart > 0;
    const canMoveForward = visibleQuarterStart + VISIBLE_QUARTER_COUNT < calendar.quarters.length;
    const visibleYears = new Set(visibleQuarters.map((quarter) => quarter.year));

    function applyRange() {
        // Apply sends the chosen dates and grouping together, so the graph cannot
        // receive week boundaries while still configured to display daily periods.
        if (draft.firstIndex == null || draft.lastIndex == null) return;
        onApply(resolution, periods[draft.firstIndex], periods[draft.lastIndex]);
    }

    function requestClose() {
        // The dialog's close action asks before losing unapplied date changes.
        // Its Cancel button discards those changes directly without this confirmation.
        const shouldClose = !isDirty || window.confirm('Discard the changes to this date range?');
        if (shouldClose) onClose();
    }

    return (
        <Dialog title='Select report date range' onClose={requestClose} large>
            <div className='report-range-dialog'>
                <div className='report-range-dialog__selection'>
                    <RangeBoundary
                        label='Start'
                        valueRef={preview.startValueRef}
                        value={formatBoundary(draft.firstIndex, periods, resolution, 'start')}
                    />
                    <div className='report-range-dialog__arrow' aria-hidden='true'>to</div>
                    <RangeBoundary
                        label='End'
                        valueRef={preview.endValueRef}
                        value={formatBoundary(draft.lastIndex, periods, resolution, 'end')}
                    />
                </div>

                <RangeToolbar
                    resolution={resolution}
                    hideResolution={hideResolution}
                    onResolutionChange={changeResolution}
                    onQuickRange={applyQuickRange}
                    onClear={clearRange}
                />

                <CalendarNavigation
                    years={calendar.years}
                    visibleYears={visibleYears}
                    canMoveBackward={canMoveBackward}
                    canMoveForward={canMoveForward}
                    onMove={moveCalendarWindow}
                    onShowYear={showYear}
                />

                <div
                    className={`report-calendar-timeline report-calendar-timeline--${resolution.toLowerCase()}`}
                    onPointerOver={preview.handlePointerOver}
                    onPointerMove={preview.handlePointerMove}
                    onPointerLeave={preview.resetPreview}
                    onClick={handleCalendarClick}
                >
                    <div className='report-calendar-timeline__track'>
                        {visibleQuarters.map((quarter) => (
                            <div
                                className={`report-calendar-quarter ${quarter.quarter === 1 ? 'year-break' : ''}`}
                                data-year={quarter.year}
                                data-quarter={quarter.quarter}
                                key={quarter.key}
                            >
                                <div className='report-calendar-quarter__heading'>
                                    {quarter.quarter === 1 && <strong>{quarter.year}</strong>}
                                    <span>Q{quarter.quarter}</span>
                                </div>
                                {quarter.months.map((month) => (
                                    <CalendarMonth
                                        key={month.toISODate()}
                                        month={month}
                                        resolution={resolution}
                                        periodIndexByStart={periodIndexByStart}
                                        draft={draft}
                                        todayIsoDate={todayIsoDate}
                                        registerCell={preview.registerCell}
                                    />
                                ))}
                            </div>
                        ))}
                    </div>
                </div>

                <div ref={preview.hoverTipRef} className='report-calendar-hover-tip' role='status' />
                {choicePrompt && (
                    <div
                        className='report-range-choice'
                        style={{ left: choicePrompt.x + 10, top: choicePrompt.y + 10 }}
                    >
                        <strong>Use this period as:</strong>
                        <button type='button' onClick={() => chooseBoundary('start')}>Start date</button>
                        <button type='button' onClick={() => chooseBoundary('end')}>End date</button>
                        <button type='button' className='cancel' onClick={() => setChoicePrompt(null)}>Cancel</button>
                    </div>
                )}

                <div className='dialog--button-bar report-range-dialog__actions'>
                    <button type='button' onClick={onClose}>Cancel</button>
                    <button
                        type='button'
                        className={isDirty && canApply ? 'report-range-dialog__apply changed' : undefined}
                        disabled={!canApply}
                        onClick={applyRange}
                    >
                        Apply range
                    </button>
                </div>
            </div>
        </Dialog>
    );
}
